import type { StyleSpecification } from "maplibre-gl";
import { boundsOf, type MapFeature, type Position, type ViewportSize } from "./municipality-map-layout";

export const BASEMAP_STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";

/** The existing GeoRisk query requests outSR=4326. GeoJSON order is longitude, latitude. */
export function geographicMapBounds(features: readonly MapFeature[]): [Position, Position] | null {
  const points = features.flatMap(({ geometry }) =>
    geometry.type === "Polygon" ? geometry.coordinates.flat() : geometry.coordinates.flat(2),
  );
  if (!points.length) return null;
  if (points.some(([longitude, latitude]) => !Number.isFinite(longitude) || !Number.isFinite(latitude) ||
    Math.abs(longitude) > 180 || Math.abs(latitude) > 85.051129)) {
    throw new Error("Boundary coordinates must be WGS84 longitude/latitude for the geographic basemap.");
  }
  const bounds = boundsOf(points);
  return [[bounds.minX, bounds.minY], [bounds.maxX, bounds.maxY]];
}

export function municipalityFitOptions(viewport: ViewportSize) {
  return {
    padding: {
      top: 12 + viewport.height * 0.09,
      bottom: 12 + viewport.height * 0.09,
      left: 12 + viewport.width * 0.09,
      right: 12 + viewport.width * 0.09,
    },
    duration: 0,
    maxZoom: 16,
  };
}

/** Keep real roads, water, landcover and major places; omit competing village/POI labels and 3D. */
export function dashboardBasemapStyle(style: StyleSpecification): StyleSpecification {
  return {
    ...style,
    projection: { type: "mercator" },
    terrain: undefined,
    layers: style.layers.filter((layer) => layer.type !== "fill-extrusion" &&
      !(layer.type === "symbol" && (
        ("source-layer" in layer && layer["source-layer"] === "poi") ||
        ["label_other", "label_village"].includes(layer.id)
      ))),
  };
}
